import { Map, latLngBounds, LatLngBounds, Point } from 'leaflet'
import { type tileStoreData, get as getMapTile } from '@/libs/leaflet/indexedDB'
/**
 * 指定した範囲のタイル画像Urlを特定のズームレベルまですべて取得する
 * param map leafletMapオブジェクト
 * param swLng 取得対象の短形範囲(南西緯度)
 * param neLng 取得対象の短形範囲(北東緯度)
 * param zoom ズームレベル(例:18を指定した場合、レベル1~18まですべてのズームレベルのタイル画像が返却される)
 * @returns 地図画像タイルのurl
 */
export const getAllTileUrls = (
  map: Map,
  swLng: L.LatLng,
  neLng: L.LatLng,
  zoom: number
): string[] => {
  const tilesUrls = (() => {
    let retUrls: string[] = []
    for (let z = 1; z <= zoom; z++) {
      const tiles = getTileUrls(map, swLng, neLng, z)
      retUrls = [...retUrls, ...tiles]
    }
    return retUrls
  })()
  return tilesUrls
}

/**
 * 指定した範囲のタイル画像Urlを取得する
 * param map leafletMapオブジェクト
 * param swLng 取得対象の短形範囲(南西緯度)
 * param neLng 取得対象の短形範囲(北東緯度)
 * param zoom ズームレベル
 * @returns 地図画像タイルのurl
 */
export const getTileUrls = (map: Map, swLng: L.LatLng, neLng: L.LatLng, zoom: number): string[] => {
  const bounds = latLngBounds(swLng, neLng)
  const tileBounds = getTileBounds(map, bounds, zoom)
  const baseUrl = 'https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png'

  const tileUrls: string[] = []
  for (let x = tileBounds.swTile.x; x <= tileBounds.neTile.x; x++) {
    for (let y = tileBounds.neTile.y; y <= tileBounds.swTile.y; y++) {
      const url = baseUrl
        .replace('{z}', zoom.toString())
        .replace('{x}', x.toString())
        .replace('{y}', y.toString())
      tileUrls.push(url)
    }
  }
  return tileUrls
}

/**
 * 指定した範囲の北東、南西のタイル座標を取得する
 * param map leafletMapオブジェクト
 * param latLngBounds 取得対象の短形範囲
 * param zoom number タイルのz座標（ズームレベル）
 * @returns 北東、南西のタイル座標
 */
export const getTileBounds = (
  map: Map,
  latLngBounds: LatLngBounds,
  zoom: number
): { neTile: Point; swTile: Point } => {
  // ピクセル座標を取得し、256で割り該当するタイル座標を計算タイル座標を計算
  const neTile = map.project(latLngBounds.getNorthEast(), zoom).divideBy(256).floor()
  const swTile = map.project(latLngBounds.getSouthWest(), zoom).divideBy(256).floor()
  return { neTile, swTile }
}

/**
 * 指定したurlのタイル画像をダウンロード
 * param url 取得対象のタイル画像url
 * @returns tileStoreData
 */
export const downloadTileImage = async (url: string): Promise<tileStoreData> => {
  return await fetch(url, {
    method: 'GET',
    headers: {}
  })
    .then(async response => {
      const key = response.url
      const coordinate = (() => {
        const re =
          /https:\/\/cyberjapandata\.gsi\.go\.jp\/xyz\/[^/]+\/([0-9]+)\/([0-9]+)\/([0-9]+)\.([a-zA-Z]+)/
        const match = key.match(re)
        if (match === null) return null
        const x = match[1]
        const y = match[2]
        const z = match[3]
        const ext = match[4]
        return { x, y, z, ext }
      })()
      if (coordinate !== null) {
        const buffer = await response
          .arrayBuffer()
          .then(async buffer => {
            return buffer
          })
          .catch(err => {
            throw new Error(err)
          })
        const data: tileStoreData = {
          arrayBuffer: buffer.slice(0),
          // UNIXタイムスタンプ形式で設定
          createdAt: Math.round(new Date().getTime() / 1000),
          mine: `image/${coordinate.ext.replace('.', '')}`,
          url: key,
          key: key,
          x: coordinate.x,
          y: coordinate.y,
          z: coordinate.z
        }
        return data
      }
      throw new Error('地図画像のURLが不正です。')
    })
    .catch(err => {
      throw new Error(err)
    })
}

/**
 * 指定したurlのタイル画像をindexedDBからBlob形式で取得する
 * param id 取得対象のタイル画像url
 * @returns Promise
 */
export const getImageBlob = async (id: string): Promise<Blob | undefined> => {
  const res = await getMapTile<tileStoreData>('tileStore', id).catch(err => {
    console.error(err)
    return undefined
  })
  if (res !== undefined) {
    const blob = new Blob([res.arrayBuffer], { type: res.mine })
    return blob
  }
  return undefined
}
